import { Button, DialogContent, Divider } from '@mui/material';
import {
  athleteDisplayName,
  getWorkoutName,
  getWorkoutStatus,
} from '@strength/domain';
import { formatISODate } from '@strength/utils';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/badge/badge';
import { Drawer, DrawerFooter, DrawerHeader } from '../../components/drawer';
import { DisabledWithTooltip } from '../../components/permissions/DisabledWithTooltip';
import { useViewer } from '../../context/viewer';
import { getLocale } from '../../utils/i18n';
import { Note } from './types';

export function NotesSheet({
  open,
  note,
  onSave,
  onClose,
}: {
  open: boolean;
  note: Note;
  onSave: (id: string, resolved: boolean) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const canEditNotes = hasPermissions('notes', 'edit');
  return (
    <Drawer open={open} onClose={() => onClose()} anchor="right">
      <DrawerHeader onClose={() => onClose()}>
        <div className="flex flex-row items-center gap-2">
          <div className="text-xl font-semibold">{t('note')}</div>
          {note.resolved && <Badge label={t('archived')} color="gray" />}
        </div>
      </DrawerHeader>
      <DialogContent className="flex flex-col space-y-4">
        <div className="font-bold">{t('details')}</div>
        <LabeledValue
          label={t('name')}
          value={athleteDisplayName({
            givenName: note.athleteFirstName,
            familyName: note.athleteLastName,
          })}
        />
        <LabeledValue
          label={t('date')}
          value={
            note.createdAt
              ? formatISODate(note.createdAt, getLocale(), {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                })
              : ''
          }
        />
        <LabeledValue label={t('team')} value={note.teamName} />
        {note.exerciseName && (
          <LabeledValue label={t('exercise')} value={note.exerciseName} />
        )}
        <LabeledValue
          label={t('workout')}
          value={getWorkoutName(
            {
              cycleSortOrder: note.cycleSortOrder,
              sessionSortOrder: note.sessionSortOrder,
              name: note.workoutName,
            },
            {
              name: note.blockName,
              cycleLabel: note.cycleLabel,
              sessionLabel: note.sessionLabel,
            },
          )}
        />
        <LabeledValue
          label={t('workoutStatus')}
          value={getWorkoutStatus(note.workoutStatus)}
        />
        <Divider sx={{ marginTop: '24px', marginBottom: '24px' }} flexItem />
        <div className="font-bold">{t('note')}</div>
        <div className="text-sm whitespace-pre-line">{note.note}</div>
      </DialogContent>

      <DrawerFooter className="flex flex-row w-full p-8 justify-end gap-2">
        <Button variant="outlined" onClick={onClose}>
          {t('close')}
        </Button>
        <DisabledWithTooltip
          disabled={!canEditNotes}
          reason={t('permissionDisabled.archive', { item: t('notes') })}
        >
          <Button onClick={() => onSave(note.alertId, !note.resolved)}>
            {note.resolved ? t('unarchiveNote') : t('archiveNote')}
          </Button>
        </DisabledWithTooltip>
      </DrawerFooter>
    </Drawer>
  );
}

function LabeledValue({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <div className="flex flex-row items-center w-full">
      <div className="flex grow w-full ">
        <span className="grow w-full text-black/60">{label}</span>
        <span className="grow w-full font-medium">{value}</span>
      </div>
    </div>
  );
}
